/**
 * Wishlist page (sections/main-wishlist.liquid). Renders the local wishlist
 * (assets/wishlist.js) and handles "Move to Cart": pick a size in a modal (bottom
 * sheet on mobile), add that variant, then drop it from the list.
 *
 * The list only stores ids + handles. Every item's live data comes from ONE
 * Storefront API request (`nodes`), priced for the visitor's market via
 * @inContext; the same response feeds the size picker. Cards are cloned from the
 * section's <template>, which mirrors snippets/product-card.liquid.
 *
 * Products must be published to the token's sales channel (Headless). Ones the
 * API can't see are hidden, not deleted, so a channel gap never wipes a list.
 */
import { ThemeEvents } from '@theme/events';
import { wishlist, STORAGE_KEY } from '@theme/wishlist';

const config = window.wishlistPageConfig || {};
const grid = document.getElementById('wishlist-grid');
const modal = document.querySelector('[data-wishlist-modal]');
/** Tile widths per breakpoint — the 'quarter' row of snippets/image-widths.liquid. */
const IMAGE_WIDTHS = { mobile: 400, tablet: 550, desktop: 500 };
let modalSelection = { item: null, variantId: null };

const WISHLIST_QUERY = `
  query Wishlist($ids: [ID!]!, $country: CountryCode, $language: LanguageCode)
  @inContext(country: $country, language: $language) {
    nodes(ids: $ids) {
      ... on Product {
        id
        handle
        title
        productType
        availableForSale
        images(first: 2) { nodes { url altText } }
        priceRange { minVariantPrice { amount currencyCode } }
        compareAtPriceRange { minVariantPrice { amount currencyCode } }
        options { name }
        variants(first: 100) { nodes { id availableForSale selectedOptions { name value } } }
      }
    }
  }
`;

const productGid = (productId) => `gid://shopify/Product/${productId}`;
const numericId = (gid) => String(gid).split('/').pop();

function productUrl(handle) {
  const root = window.Shopify?.routes?.root || '/';
  return `${root}products/${encodeURIComponent(handle)}`;
}

/** @returns {Promise<Map<string, Object>>} products by numeric id; missing ones are absent. */
async function fetchProducts(items) {
  const { token, apiVersion, country, language } = window.themeStorefront || {};
  const response = await fetch(`/api/${apiVersion}/graphql.json`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'X-Shopify-Storefront-Access-Token': token },
    body: JSON.stringify({
      query: WISHLIST_QUERY,
      variables: { ids: items.map((item) => productGid(item.productId)), country, language }
    })
  });
  if (!response.ok) throw new Error(`Storefront API ${response.status}`);
  const { data, errors } = await response.json();
  if (errors?.length) console.warn('[wishlist-page] Storefront API:', errors);
  if (!data) throw new Error('Storefront API returned no data');
  return new Map((data?.nodes || []).filter(Boolean).map((product) => [numericId(product.id), product]));
}

function sizedImage(url, width) {
  const sized = new URL(url, window.location.origin);
  sized.searchParams.set('width', String(width));
  return sized.toString();
}

function formatMoney({ amount, currencyCode }) {
  const locale = `${document.documentElement.lang || 'en'}-u-nu-latn`;
  return new Intl.NumberFormat(locale, { style: 'currency', currency: currencyCode }).format(Number(amount));
}

function setLoading(element, isLoading) {
  if (!element) return;
  element.classList.toggle('is-loading', isLoading);
  element.disabled = isLoading;
}

function updateCount(count) {
  const counter = document.querySelector('.wishlist-page__count');
  if (counter) counter.textContent = count ? `(${count})` : '';
}

function toggleEmpty(isEmpty) {
  const empty = document.querySelector('.wishlist-page__empty');
  if (empty) empty.hidden = !isEmpty;
  grid.hidden = isEmpty;
}

/* ------------------------------------------------------------ Cart + move -- */

function refreshCartBubble(sections) {
  const bubble = document.getElementById('cart-icon-bubble');
  const html = sections?.['cart-icon-bubble'];
  if (!bubble || html == null) return;
  const parsed = new DOMParser().parseFromString(html, 'text/html').querySelector('.shopify-section');
  if (parsed) (bubble.querySelector('.shopify-section') || bubble).innerHTML = parsed.innerHTML;
}

async function notifyCartUpdate() {
  try {
    const response = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
    if (!response.ok) return;
    const cart = await response.json();
    document.dispatchEvent(
      new CustomEvent(ThemeEvents.cartUpdate, {
        bubbles: true,
        detail: { resource: cart, sourceId: 'wishlist', data: { source: 'wishlist' } }
      })
    );
  } catch {}
}

async function addToCartAndRemove(item, variantId, loadingElement, fromModal) {
  setLoading(loadingElement, true);
  const body = { items: [{ id: Number(variantId), quantity: 1 }] };
  if (document.getElementById('cart-icon-bubble')) {
    body.sections = 'cart-icon-bubble';
    body.sections_url = window.location.pathname;
  }

  try {
    const response = await fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body)
    });
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    const cart = await response.json();

    refreshCartBubble(cart.sections);
    document.dispatchEvent(new CustomEvent(ThemeEvents.wishlistMoveToCart, { detail: { line: cart.items?.[0] } }));
    wishlist.remove(item.productId);
    notifyCartUpdate();
    document.dispatchEvent(new CustomEvent(ThemeEvents.cartToastShow, { bubbles: true, detail: { message: config.movedToast } }));
    if (fromModal) closeModal();
  } catch (error) {
    console.error('[wishlist-page] move to cart failed:', error);
  } finally {
    setLoading(loadingElement, false);
  }
}

function onMove(item, product, moveButton) {
  const variants = product.variants.nodes;
  if (variants.length > 1) {
    openModal(item, product);
    return;
  }
  addToCartAndRemove(item, numericId(variants[0]?.id || item.variantId), moveButton, false);
}

/* ------------------------------------------------------------------ Modal -- */

/** One chip per size, preferring an available variant for each. */
function buildSizes(product) {
  const optionName = product.options.find((option) => option.name.toLowerCase() === 'size')?.name || product.options[0]?.name;
  const sizes = new Map();
  product.variants.nodes.forEach((variant) => {
    const label = variant.selectedOptions.find((option) => option.name === optionName)?.value || variant.id;
    const existing = sizes.get(label);
    if (!existing || (variant.availableForSale && !existing.available)) {
      sizes.set(label, { label, id: numericId(variant.id), available: variant.availableForSale });
    }
  });
  return [...sizes.values()];
}

function setText(selector, value) {
  const element = modal.querySelector(selector);
  if (element) element.textContent = value || '';
}

function openModal(item, product) {
  if (!modal) return;
  modalSelection = { item, variantId: null };

  const thumb = modal.querySelector('[data-wishlist-modal-thumb]');
  const image = product.images.nodes[0];
  if (thumb) thumb.src = image ? sizedImage(image.url, 144) : '';
  setText('[data-wishlist-modal-title]', product.title);
  const typeElement = modal.querySelector('[data-wishlist-modal-type]');
  if (typeElement) {
    typeElement.textContent = product.productType;
    typeElement.hidden = !product.productType;
  }
  const priceElement = modal.querySelector('[data-wishlist-modal-price]');
  if (priceElement) {
    const price = product.priceRange.minVariantPrice;
    const compare = product.compareAtPriceRange.minVariantPrice;
    priceElement.textContent = formatMoney(price);
    if (Number(compare.amount) > Number(price.amount)) {
      const compareElement = document.createElement('s');
      compareElement.textContent = formatMoney(compare);
      priceElement.appendChild(compareElement);
    }
  }
  setText('[data-wishlist-modal-prompt]', config.sizePrompt);
  setText('[data-wishlist-modal-atc-text]', config.addLabel);

  const addButton = modal.querySelector('[data-wishlist-modal-atc]');
  addButton.disabled = true;

  const sizesWrap = modal.querySelector('[data-wishlist-modal-sizes]');
  sizesWrap.replaceChildren(
    ...buildSizes(product).map((size) => {
      const chip = document.createElement('button');
      chip.type = 'button';
      chip.className = 'wishlist-modal__size';
      chip.textContent = size.label;
      chip.disabled = !size.available;
      chip.addEventListener('click', () => {
        sizesWrap.querySelectorAll('.wishlist-modal__size').forEach((other) => other.classList.remove('is-selected'));
        chip.classList.add('is-selected');
        modalSelection.variantId = size.id;
        addButton.disabled = false;
      });
      return chip;
    })
  );

  modal.hidden = false;
  document.body.style.overflow = 'hidden';
  document.addEventListener('keydown', onModalKeydown);
}

function closeModal() {
  if (!modal) return;
  modal.hidden = true;
  document.body.style.overflow = '';
  document.removeEventListener('keydown', onModalKeydown);
  modalSelection = { item: null, variantId: null };
}

function onModalKeydown(event) {
  if (event.key === 'Escape') closeModal();
}

if (modal) {
  modal.querySelectorAll('[data-wishlist-modal-close]').forEach((element) => element.addEventListener('click', closeModal));
  const addButton = modal.querySelector('[data-wishlist-modal-atc]');
  addButton?.addEventListener('click', () => {
    const { item, variantId } = modalSelection;
    if (item && variantId) addToCartAndRemove(item, variantId, addButton, true);
  });
}

/* -------------------------------------------------------------- Rendering -- */

function fillPicture(picture, image, title) {
  picture.querySelector('[data-hook="source-mobile"]')?.setAttribute('srcset', sizedImage(image.url, IMAGE_WIDTHS.mobile));
  picture.querySelector('[data-hook="source-tablet"]')?.setAttribute('srcset', sizedImage(image.url, IMAGE_WIDTHS.tablet));
  const img = picture.querySelector('img');
  img.src = sizedImage(image.url, IMAGE_WIDTHS.desktop);
  img.alt = image.altText || title;
}

function buildCard(item, product) {
  const template = document.getElementById('wishlist-card-template');
  const card = template.content.firstElementChild.cloneNode(true);
  const url = productUrl(product.handle);
  const [primaryImage, secondaryImage] = product.images.nodes;

  card.dataset.productId = item.productId;
  card.querySelectorAll('[data-hook~="link"]').forEach((link) => link.setAttribute('href', url));
  card.querySelector('[data-hook~="title"]').textContent = product.title;

  const primaryPicture = card.querySelector('.product-card__img:not(.product-card__img--secondary)');
  if (primaryImage) fillPicture(primaryPicture, primaryImage, product.title);
  else primaryPicture.remove();
  const secondaryPicture = card.querySelector('[data-hook="secondary"]');
  if (secondaryImage) fillPicture(secondaryPicture, secondaryImage, product.title);
  else secondaryPicture.remove();

  const typeElement = card.querySelector('[data-hook="type"]');
  if (product.productType) typeElement.textContent = product.productType;
  else typeElement.remove();

  const price = product.priceRange.minVariantPrice;
  const compare = product.compareAtPriceRange.minVariantPrice;
  const onSale = product.availableForSale && Number(compare.amount) > Number(price.amount);
  const priceElement = card.querySelector('[data-hook="price"]');
  priceElement.textContent = formatMoney(price);
  priceElement.classList.toggle('product-card__price--sale', onSale);
  const compareElement = card.querySelector('[data-hook="compare"]');
  if (onSale) compareElement.textContent = formatMoney(compare);
  else compareElement.remove();

  if (product.availableForSale) card.querySelector('[data-hook="soldout"]').remove();

  const removeButton = card.querySelector('[data-wishlist-remove]');
  removeButton.setAttribute('aria-label', `${config.removeLabel}: ${product.title}`);
  removeButton.addEventListener('click', () => wishlist.remove(item.productId));

  const moveButton = card.querySelector('[data-wishlist-move]');
  card.querySelector('[data-hook="move-text"]').textContent = product.availableForSale ? config.moveLabel : config.soldOutLabel;
  moveButton.disabled = !product.availableForSale;
  moveButton.addEventListener('click', () => onMove(item, product, moveButton));

  return card;
}

function removeCard(card) {
  card?.remove();
  const remaining = grid.querySelectorAll('[data-wishlist-item]').length;
  updateCount(remaining);
  if (remaining === 0) toggleEmpty(true);
}

function renderSkeletons(count) {
  const skeleton = document.getElementById('wishlist-skeleton-template');
  grid.replaceChildren(...Array.from({ length: count }, () => skeleton.content.firstElementChild.cloneNode(true)));
  grid.setAttribute('aria-busy', 'true');
}

async function render() {
  const items = wishlist.items();
  if (items.length === 0) {
    grid.replaceChildren();
    grid.setAttribute('aria-busy', 'false');
    updateCount(0);
    toggleEmpty(true);
    return;
  }

  toggleEmpty(false);
  renderSkeletons(Math.min(items.length, 8));

  let products = new Map();
  let loaded = false;
  try {
    products = await fetchProducts(items);
    loaded = true;
  } catch (error) {
    console.error('[wishlist-page] could not load products:', error);
  }

  const live = items.filter((item) => products.has(item.productId));
  // Not sold in this market (e.g. licensed products outside the UAE): kept saved,
  // but left out here and in the header count.
  if (loaded) {
    wishlist.recordAvailability(
      live.map((item) => item.productId),
      items.filter((item) => !products.has(item.productId)).map((item) => item.productId)
    );
  }
  grid.replaceChildren(...live.map((item) => buildCard(item, products.get(item.productId))));
  grid.setAttribute('aria-busy', 'false');
  updateCount(live.length);
  toggleEmpty(live.length === 0);

  document.dispatchEvent(
    new CustomEvent(ThemeEvents.wishlistRendered, {
      detail: {
        items: live.map((item) => {
          const product = products.get(item.productId);
          return { productId: item.productId, title: product.title, price: Number(product.priceRange.minVariantPrice.amount) };
        })
      }
    })
  );
}

if (grid) {
  render();

  // Removed here (× button, Move to Cart) or by a heart elsewhere on the page.
  document.addEventListener(ThemeEvents.wishlistChange, (event) => {
    if (event.detail?.action !== 'remove') return;
    removeCard(grid.querySelector(`[data-wishlist-item][data-product-id="${event.detail.item.productId}"]`));
  });

  // Another tab changed the list.
  window.addEventListener('storage', (event) => {
    if (event.key === STORAGE_KEY) render();
  });
}
